import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Toast } from 'bootstrap';
import IntlTelInput from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";
import { apiRequest, sendEvent } from '../utils';

import verifiedUserIcon from '../assets/icons/verified_user.svg?raw&inline';
import shareIcon from '../assets/icons/share.svg?raw&inline';

const ENVIRONMENT = import.meta.env.VITE_ENV || '';

type FieldKeys =
  | 'name'
  | 'phone'
  | 'motive'
  | 'stakeholderCompany'
  | 'stakeholderRole'
  | 'stakeholderSector';

interface BusinessRequestModalProps {
  organization: {
    id: number;
    nombre: string;
    [key: string]: any;
  };
  verifiedBusiness?: { id: number, [key: string]: any } | null;
}

export const BusinessRequestModal: React.FC<BusinessRequestModalProps> = ({
  organization,
  verifiedBusiness,
}) => {
  const { t } = useTranslation();

  /* ————————————————— State ————————————————— */
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [motive, setMotive] = useState('');
  const [stakeholderCompany, setStakeholderCompany] = useState('');
  const [stakeholderRole, setStakeholderRole] = useState('');
  const [stakeholderSector, setStakeholderSector] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const landingBusinessClaimIndexPath = `/${t('locale', { defaultValue: 'es' })}/business_claim/landing?ref=${organization.id}`;

  const initialFieldState: Record<FieldKeys, boolean> = {
    name: false,
    phone: false,
    motive: false,
    stakeholderCompany: false,
    stakeholderRole: false,
    stakeholderSector: false,
  };

  const [touched, setTouched] = useState(initialFieldState);
  const [errors, setErrors] = useState(initialFieldState);

  let toast: Toast | null = null;
  const toastElement = document.getElementById('responseToast');
  if (toastElement) toast = Toast.getOrCreateInstance(toastElement as Element);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name: fieldName, value } = event.target;
    switch (fieldName) {
      case 'name':
        setName(value);
        break;
      case 'motive':
        setMotive(value);
        break;
      case 'stakeholderCompany':
        setStakeholderCompany(value);
        break;
      case 'stakeholderRole':
        setStakeholderRole(value);
        break;
      case 'stakeholderSector':
        setStakeholderSector(value);
        break;
      default:
        break;
    }
  };

  const validateField = (field: FieldKeys, value: string): boolean => {
    switch (field) {
      case 'name':
        return value.trim() !== '' && /^[a-zA-Z\s]+$/.test(value);
      case 'motive':
        return value.trim() !== '';
      case 'stakeholderCompany':
        return value.trim() !== '';
      case 'stakeholderRole':
        return value.trim() !== '';
      case 'stakeholderSector':
        return value.trim() !== '';
      default:
        return true;
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name: fieldName, value } = e.target as { name: FieldKeys; value: string };
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    setErrors((prev) => ({ ...prev, [fieldName]: !validateField(fieldName, value) }));
  };

  const onSubmit = async () => {
    setError(null);
    setSubmitting(true);

    const data = {
      name,
      phone,
      motive,
      stakeholder_company: stakeholderCompany,
      stakeholder_role: stakeholderRole,
      stakeholder_sector: stakeholderSector,
      verified_business_id: verifiedBusiness?.id,
    };

    if (data.name === '' || data.motive === '' || !isValid || data.stakeholder_company === '' || data.stakeholder_role === '' || data.stakeholder_sector === '') {
      setError(t('kyb_flow.org_members_card.modal.error'));
      setSubmitting(false);
      return;
    }

    try {
      const res = await apiRequest('/business_claim/verified_business_request', 'POST', data);
      
      setSubmitting(false);

      if (res.success) {
        setToastMessage(res.message);
        sendEvent({ event_name: 'Solicitud de release del business', properties: { organization_id: organization.id, organization_name: organization.nombre } });

        const modal = Modal.getOrCreateInstance(document.getElementById('shareholderModal') as Element);
        modal.hide();

        // Remove all modal-backdrop elements
        const modalBackdrops = document.querySelectorAll('.modal-backdrop');
        modalBackdrops.forEach(backdrop => backdrop.remove());
      } else {
        setToastMessage(res.error);
      }
      toast?.show();
    } catch (err) {
      console.error(err);
      setSubmitting(false);
    }
  };

  const onShareButtonClick = () => {
    const url = `${window.location.origin}${landingBusinessClaimIndexPath}`;
    navigator.clipboard.writeText(url);
    setToastMessage(t('business_request_modal.unverified_business.copied'));
    sendEvent({ event_name: 'Click en compartir enlace', properties: { organization_id: organization.id, organization_name: organization.nombre } });
    toast?.show();
  };

  const handlePhoneChange = (number: string) => {
    setPhone(number);
  };

  const handlePhoneValidityChange = (valid: boolean) => {
    setIsValid(valid);
  };

  const handlePhoneBlur = () => {
    setErrors((prev) => ({ ...prev, phone: !isValid }));
    setTouched((prev) => ({ ...prev, phone: true }));
  };

  return (
    <>
      {ENVIRONMENT === 'development' && (
        <div className="language-switcher mb-4">
          <a className="section-pill-container" data-bs-target="#shareholderModal" data-bs-toggle="modal" id="shareholder-pill">
            <div className="section-pill">
              <div className="d-flex align-items-center">
                <span>Abrir modal</span>
              </div>
            </div>
          </a>
        </div>
      )}
      <div className="modal fade" 
        id={"shareholderModal"} 
        tabIndex={-1} 
        aria-labelledby={`shareholderModal`} 
        aria-hidden="true"
        data-testid="shareholderModal"
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className={`modal-header border-${verifiedBusiness ? 'bottom' : '0'}`}>
              <h5 id="shareholderModalLabel" className="modal-title size-20 font-weight-600">
                {verifiedBusiness ? 'Solicitud de acceso a directores y accionistas' : null}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>

            {verifiedBusiness ? (
              <div className="modal-body ps-4">
                {/* ——— Empresa ——— */}
                <div className="d-flex align-items-center">
                  <span 
                    className="size-32 d-flex align-items-center" 
                    dangerouslySetInnerHTML={{ __html: verifiedUserIcon }} 
                  />
                  <span className="size-20 font-weight-600 text-uppercase ms-2">
                    {organization.nombre}
                  </span>
                </div>

                {/* ——— Descripción ——— */}
                <div className="d-flex flex-column">
                  <p className="size-13 font-weight-400 mt-2 text-dark">
                    {t('business_request_modal.protected_info')}
                  </p>

                </div>

                {/* ——— Formulario ——— */}
                <div className="d-flex flex-column">
                  <h3 className="size-20 font-weight-600 mt-2 text-dark">
                    {t('business_request_modal.personal_information')}
                  </h3>
                  <div className="row px-1">
                    {error && <label className="text-danger mb-2">{error}</label>}
                  </div>
                  {/* Nombre */}
                  <div className="mb-3">
                    <label htmlFor="inputName" className="form-label">
                      {t('kyb_flow.org_members_card.modal.name')}
                    </label>
                    <input
                      className="form-control less-rounded"
                      id="inputName"
                      name="name"
                      data-testid="inputName"
                      value={name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                    {touched.name && errors.name && (
                      <p className="text-danger mb-0">
                        {t('kyb_flow.beneficiaries.invalid_name')}
                      </p>
                    )}
                  </div>

                  {/* Teléfono */}
                  <div className="mb-3 d-flex flex-column">
                    <label htmlFor="inputPhone" className="form-label">
                      {t('kyb_flow.org_members_card.modal.phone_number')}
                    </label>
                    <IntlTelInput
                      onChangeNumber={handlePhoneChange}
                      onChangeValidity={handlePhoneValidityChange}
                      inputProps={{
                        className: 'form-control less-rounded',
                        id: 'inputPhone',
                        name: 'phone',
                        'data-testid': 'inputPhone',
                        onBlur: handlePhoneBlur,
                      }}
                      initOptions={{
                        initialCountry: "pa",
                      }}
                    />
                    {touched.phone && errors.phone && (
                      <p className="text-danger mb-0">
                        {t('kyb_flow.beneficiaries.invalid_phone')}
                      </p>
                    )}
                  </div>

                  <h3 className="size-20 font-weight-600 mt-2 text-dark">
                    {t('business_request_modal.professional_information')}
                  </h3>

                  {/* stakeholderCompany */}
                  <div className="mb-3">
                    <label htmlFor="stakeholderCompany" className="form-label">
                      {t('business_request_modal.stakeholder_company.label')}
                    </label>
                    
                    <input
                      className="form-control less-rounded"
                      id="stakeholderCompany"
                      name="stakeholderCompany"
                      data-testid="inputStakeholderCompany"
                      value={stakeholderCompany}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                    {touched.stakeholderCompany && errors.stakeholderCompany && (
                      <p className="text-danger mb-0">
                        {t('business_request_modal.stakeholder_company.error')}
                      </p>
                    )}
                  </div>

                  {/* stakeholderRole */}
                  <div className="mb-3">
                    <label htmlFor="stakeholderRole" className="form-label">
                      {t('business_request_modal.stakeholder_role.label')}
                    </label>
                    <input
                      className="form-control less-rounded"
                      id="stakeholderRole"
                      name="stakeholderRole"
                      data-testid="inputStakeholderRole"
                      value={stakeholderRole}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                    {touched.stakeholderRole && errors.stakeholderRole && (
                      <p className="text-danger mb-0">
                        {t('business_request_modal.stakeholder_role.error')}
                      </p>
                    )}
                  </div>

                  {/* stakeholderSector */}
                  <div className="mb-3">
                    <label htmlFor="stakeholderSector" className="form-label">
                      {t('business_request_modal.stakeholder_sector.label')}
                    </label>
                    <input
                      className="form-control less-rounded"
                      id="stakeholderSector"
                      name="stakeholderSector"
                      data-testid="inputStakeholderSector"
                      value={stakeholderSector}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />

                    {touched.stakeholderSector && errors.stakeholderSector && (
                      <p className="text-danger mb-0">
                        {t('business_request_modal.stakeholder_sector.error')}
                      </p>
                    )}

                  </div>

                  {/* Motivo */}
                  <div className="mb-3">
                    <label htmlFor="motive" className="form-label size-14 font-weight-400">
                      {t('business_request_modal.motivo.label')}
                    </label>
                    <textarea
                      name="motive"
                      id="motive"
                      className="form-control less-rounded"
                      data-testid="inputMotive"
                      value={motive}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                    {touched.motive && errors.motive && (
                      <p className="text-danger mb-0">
                        {t('business_request_modal.motivo.error')}
                      </p>
                    )}
                  </div>

                  {/* Botón Enviar */}
                  <div className="d-flex justify-content-end">
                    <button
                      className="gaevent btn btn-dark text-white ms-1 mt-2"
                      onClick={onSubmit}
                      disabled={submitting}
                      data-testid="send-button"
                    >
                      {t('send')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ——— Empresa NO verificada ——— */
              <div className="modal-body pt-0">
                <div className="text-center">
                  <span className="size-20 font-weight-600 text-center">
                    {t('business_request_modal.title')}
                  </span>
                  <p
                    className="mx-auto size-14 text-dark text-center"
                    style={{ maxWidth: '350px' }}
                    dangerouslySetInnerHTML={{
                      __html: t('business_request_modal.unverified_business.description', {
                        path: landingBusinessClaimIndexPath,
                      }),
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-dark d-flex mx-auto"
                    onClick={onShareButtonClick}
                  >
                    <span
                      className="me-2"
                      data-testid="share-button"
                      dangerouslySetInnerHTML={{ __html: shareIcon }} 
                    />
                    {t('business_request_modal.unverified_business.button')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast global */}
      <div
        className="toast pd-toast"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        id="responseToast"
      >
        <div className="toast-body">{toastMessage}</div>
      </div>
    </>
  );
};
